import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { cn } from './cn'

export type ToastTone = 'info' | 'success' | 'error'

export interface Toast {
  id: string
  tone: ToastTone
  title: string
  description?: string
}

export interface ToastOptions {
  tone?: ToastTone
  description?: string
  /** Milliseconds before auto-dismiss. `0` pins the toast until dismissed. */
  duration?: number
}

interface ToastContextValue {
  toasts: Toast[]
  push: (title: string, options?: ToastOptions) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const DEFAULT_DURATION: Record<ToastTone, number> = {
  info: 4000,
  // Shorter than info: "Saved" has nothing left to say after a moment, and a
  // create-and-run pushes two of them.
  success: 3000,
  // Errors are pinned. A failed save that vanishes after four seconds is a
  // failed save the user does not know about.
  error: 0,
}

/**
 * How many toasts show at once. Creating three matters in a row used to leave
 * four stacked over the Queue; past this the oldest toast that is neither an
 * error nor pinned makes room.
 */
const MAX_VISIBLE = 3

let counter = 0
function nextId(): string {
  counter += 1
  return `toast-${counter}`
}

/**
 * Transient notices — "Key minted", "Could not reach the API".
 *
 * Deliberately not a place to report validation errors: those belong next to
 * the field that caused them ({@link Field}'s `error`), where the user is
 * already looking.
 *
 * At most three show at once: a fourth pushes out the oldest toast that is not
 * an error and not pinned (`duration: 0`), and a toast identical to one already
 * showing restarts that one's timer instead of stacking. Errors and pinned
 * toasts are never pushed out, and a repeat never unpins a pinned toast, so a
 * stack of only those may be longer than three.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  // The list as `push` and `dismiss` see it. Deciding what to drop or refresh
  // needs the current list synchronously, and timers must not be started or
  // cleared inside a state updater, which React may run twice.
  const current = useRef<Toast[]>([])
  // Toasts the caller asked to keep until dismissed (an effective duration of
  // 0). Errors are pinned by default; an info toast carrying per-matter detail
  // can be pinned too, and neither the cap nor a repeat may quietly undo that.
  const pinned = useRef(new Set<string>())

  const commit = useCallback((next: Toast[]) => {
    current.current = next
    setToasts(next)
  }, [])

  const clearTimer = useCallback((id: string) => {
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id)
      pinned.current.delete(id)
      commit(current.current.filter((toast) => toast.id !== id))
    },
    [clearTimer, commit],
  )

  /** (Re)start the auto-dismiss timer; a duration of 0 leaves the toast pinned. */
  const arm = useCallback(
    (id: string, duration: number) => {
      clearTimer(id)
      if (duration > 0) {
        pinned.current.delete(id)
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        )
      } else {
        pinned.current.add(id)
      }
    },
    [clearTimer, dismiss],
  )

  const push = useCallback(
    (title: string, options: ToastOptions = {}) => {
      const tone = options.tone ?? 'info'
      const description = options.description || undefined
      const duration = options.duration ?? DEFAULT_DURATION[tone]

      // The same notice again (a create-and-run that repeats, a double click)
      // is the same news: keep the one on screen and let it live a little longer.
      const twin = current.current.find(
        (toast) =>
          toast.tone === tone && toast.title === title && toast.description === description,
      )
      if (twin) {
        // A pinned twin stays pinned: a repeat with a default duration must not
        // turn a notice the person has to dismiss into one that fades.
        if (!pinned.current.has(twin.id)) arm(twin.id, duration)
        return twin.id
      }

      const id = nextId()
      const toast: Toast = { id, tone, title, ...(description ? { description } : {}) }
      let next = [...current.current, toast]

      // Make room from the toasts already showing, never from the arrival: a
      // notice that is dropped the moment it appears is one nobody saw. When
      // everything showing is an error or pinned nothing is dropped and the
      // stack grows.
      while (next.length > MAX_VISIBLE) {
        const oldest = next.find(
          (shown) => shown.id !== id && shown.tone !== 'error' && !pinned.current.has(shown.id),
        )
        if (!oldest) break
        clearTimer(oldest.id)
        next = next.filter((shown) => shown.id !== oldest.id)
      }

      commit(next)
      arm(id, duration)
      return id
    },
    [arm, clearTimer, commit],
  )

  // Unmounting with timers outstanding would fire setState on a dead tree.
  const timersRef = timers
  useEffect(() => {
    const map = timersRef.current
    return () => {
      for (const timer of map.values()) clearTimeout(timer)
      map.clear()
    }
  }, [timersRef])

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, push, dismiss }),
    [toasts, push, dismiss],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

const TONE_CLASS: Record<ToastTone, string> = {
  info: 'border-divider bg-surface text-ink',
  success: 'border-ok-line bg-ok-bg text-ok-fg',
  error: 'border-block-line bg-block-bg text-block-fg',
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[]
  onDismiss: (id: string) => void
}) {
  return (
    <div
      // `polite`, not `assertive`: a save confirmation should not interrupt
      // someone mid-sentence in the editor.
      aria-live="polite"
      aria-relevant="additions"
      className="pointer-events-none fixed bottom-5 right-5 z-50 flex w-[min(380px,calc(100vw-2.5rem))] flex-col gap-2"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className={cn(
            'pointer-events-auto flex items-start gap-3 rounded-lg border p-3 shadow-md',
            TONE_CLASS[toast.tone],
          )}
        >
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">{toast.title}</div>
            {toast.description ? (
              <div className="mt-0.5 text-meta opacity-80">{toast.description}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => onDismiss(toast.id)}
            aria-label="Dismiss"
            className="-mr-1 -mt-1 shrink-0 rounded px-1.5 text-base leading-none opacity-60 hover:opacity-100"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  )
}

// The hook and its provider are one unit; splitting them across files buys
// nothing but an extra import, so fast refresh loses this file.
// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used inside <ToastProvider>.')
  }
  return context
}
